import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ErrorCode, lifecycleLabels } from '@brimo/shared';
import { Banner, Button, Dialog, FieldGroup, RadioCard, Select, TextField } from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { useWorkspace } from '../../lib/session';
import { bulkContacts, changeLifecycle } from './api';
import { formatDayKey } from './format';
import { initialLifecycleTarget, lifecycleTargetsFor, type LifecycleTarget } from './lifecycleTargets';
import { useKnownUsers, type KnownUser } from './users';
import type { BulkResult, LifecyclePill, LifecycleResponse } from './types';
import styles from './dialogs.module.css';

export type { LifecycleTarget } from './lifecycleTargets';

/** Follow-up mandatory (follow_up, not_reached) or optional (the end states get "Wiedervorlage"). */
const FOLLOW_UP_REQUIRED: ReadonlySet<LifecycleTarget> = new Set(['follow_up', 'not_reached']);

export interface LifecycleSubject {
  id: string;
  name: string;
  lifecycle: LifecyclePill | null;
  owner: KnownUser | null;
}

export interface LifecycleDialogProps {
  open: boolean;
  /** One contact (record, board) or several (Kontakte 02 bulk action). */
  subjects: LifecycleSubject[];
  /** Target preselected by the board drop; defaults to "Follow Up" (or the first stage if that is current). */
  preselect?: LifecycleTarget | undefined;
  onClose: () => void;
  onDone: (result: {
    transition: LifecycleTarget;
    single: LifecycleResponse | null;
    bulk: BulkResult | null;
  }) => void;
}

interface FieldErrors {
  reason?: string | undefined;
  dueOn?: string | undefined;
  assigneeId?: string | undefined;
  transition?: string | undefined;
}

function taskTitle(target: LifecycleTarget, name: string): string {
  return FOLLOW_UP_REQUIRED.has(target) ? `Follow-up: ${name}` : `Wiedervorlage: ${name}`;
}

/** Kontakte 06 · „Lifecycle ändern": target state, reason and the mandatory follow-up. */
export function LifecycleDialog({ open, subjects, preselect, onClose, onDone }: LifecycleDialogProps) {
  const workspace = useWorkspace();
  const first = subjects[0];
  const single = subjects.length === 1 ? first : null;
  const users = useKnownUsers(useMemo(() => subjects.flatMap((s) => (s.owner ? [s.owner] : [])), [subjects]));
  const currentKey = single?.lifecycle?.key ?? null;
  const targets = useMemo(() => lifecycleTargetsFor(currentKey), [currentKey]);
  const [target, setTarget] = useState<LifecycleTarget>(() => initialLifecycleTarget(currentKey, preselect));
  const [reason, setReason] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTarget(initialLifecycleTarget(currentKey, preselect));
    setReason('');
    setDueOn('');
    setDueTime('');
    setAssigneeId(single?.owner?.id ?? workspace.user.id);
    setErrors({});
    setBanner(null);
  }, [open, preselect, single, currentKey, workspace.user.id]);

  if (!first) return null;
  const followUpRequired = FOLLOW_UP_REQUIRED.has(target);
  const assignee = users.find((user) => user.id === assigneeId);
  const title = single ? single.name : `${subjects.length} Kontakte`;
  const fromLabel = single?.lifecycle?.label ?? null;
  const subtitle = fromLabel ? `${fromLabel} → ${lifecycleLabels[target]}` : lifecycleLabels[target];
  const hasFollowUp = dueOn !== '';

  async function submit(event: FormEvent) {
    event.preventDefault();
    const next: FieldErrors = {};
    if (reason.trim() === '') next.reason = 'Bitte geben Sie einen Grund an.';
    if (followUpRequired && dueOn === '') next.dueOn = 'Pflichtangabe.';
    setErrors(next);
    setBanner(null);
    if (Object.keys(next).length > 0) return;
    const followUp = hasFollowUp
      ? { dueOn, ...(dueTime ? { dueTime } : {}), ...(assigneeId ? { assigneeId } : {}) }
      : undefined;
    const body = {
      transition: target,
      ...(reason.trim() ? { reason: reason.trim() } : {}),
      ...(followUp ? { followUp } : {}),
    };
    setPending(true);
    try {
      if (single) {
        const result = await changeLifecycle(single.id, body);
        onDone({ transition: target, single: result, bulk: null });
      } else {
        const result = await bulkContacts({
          ids: subjects.map((s) => s.id),
          action: 'change_lifecycle',
          lifecycle: body,
        });
        onDone({ transition: target, single: null, bulk: result });
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 400) {
        const fields = error.fields;
        setErrors({
          reason: fields['reason'],
          dueOn: fields['followUp.dueOn'],
          assigneeId: fields['followUp.assigneeId'],
          transition: fields['transition'],
        });
        if (
          !fields['reason'] &&
          !fields['followUp.dueOn'] &&
          !fields['followUp.assigneeId'] &&
          !fields['transition']
        ) {
          setBanner(error.message);
        }
      } else if (error instanceof ApiError && error.code === ErrorCode.TRANSITION_NOT_ALLOWED) {
        const missing = error.details?.['missing'];
        setBanner(Array.isArray(missing) && typeof missing[0] === 'string' ? missing[0] : error.message);
      } else {
        setBanner(errorMessage(error));
      }
    } finally {
      setPending(false);
    }
  }

  const formId = 'lifecycle-dialog-form';
  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow="Lifecycle ändern"
      title={title}
      subtitle={subtitle}
      width={600}
      initialFocus="first"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending}>
            Speichern
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <FieldGroup legend="Neuer Status" columns={3} gap={8} error={errors.transition}>
          {targets.map((key) => (
            <RadioCard
              key={key}
              name="lifecycle-target"
              value={key}
              title={lifecycleLabels[key]}
              checked={target === key}
              onChange={() => setTarget(key)}
            />
          ))}
        </FieldGroup>
        <TextField
          label="Grund"
          required
          value={reason}
          error={errors.reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <div className={styles.threeColumns}>
          <TextField
            label="Wiedervorlage am"
            type="date"
            required={followUpRequired}
            value={dueOn}
            error={errors.dueOn}
            onChange={(event) => setDueOn(event.target.value)}
          />
          <TextField
            label="Uhrzeit"
            type="time"
            value={dueTime}
            onChange={(event) => setDueTime(event.target.value)}
          />
          <Select
            label="Zuständig"
            value={assigneeId}
            error={errors.assigneeId}
            options={users.map((user) => ({ value: user.id, label: user.shortName }))}
            onChange={(event) => setAssigneeId(event.target.value)}
          />
        </div>
        {hasFollowUp ? (
          <Banner tone="info">
            Es entsteht die Task „{taskTitle(target, single ? single.name : 'Kontakt')}“ am{' '}
            {formatDayKey(dueOn).slice(0, 6)}
            {dueTime ? `, ${dueTime}` : ''}
            {assignee ? ` bei ${assignee.shortName}` : ''}. Ohne Folgeschritt lässt DAMOKLES keinen Kontakt
            zurück.
          </Banner>
        ) : null}
      </form>
    </Dialog>
  );
}
